import { ComponentProps, FC, Fragment, memo, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Tooltip } from '@mui/material';

import { CheckFileRequest, FileRevisionID, FileState, PresignedUrl } from '@playq/octopus2-files';
import { GenericFailure, GenericFailureCode } from '@playq/services-shared';

import { UnknownPreview } from '/shared/FilePreview/previewComponents';
import { useCheckFile } from '/api';

import { IFallbackProps, IFilePreviewProps } from './types';
import { ThumbnailPreview } from './ThumbnailPreview';
import { SourcePreview } from './SourcePreview';
import { FullScreenPreview } from './FullScreenPreview';
import { TooltipContainerStyled } from './styles';

export const FilePreview: FC<IFilePreviewProps> = memo((props) => {
  const {
    source,
    missed: propsMissed,
    sourceName = 'Unknown File',
    thumbnails,
    tooltip,
    showTooltip = true,
    Fallback,
    allowFullPreview = true,
    tooltipPlacement = 'bottom',
    isPackage = false,
    shouldCheck = false,
    onClick,
    onRenderFailure,
    onRebuildThumbnail,
    onCheck,
    ...rest
  } = props;

  const [fullPreview, setFullPreview] = useState(false);
  const [failed, setFailed] = useState(false);
  const [missed, setMissed] = useState<boolean | 'checking'>('checking');

  const handleRenderFailure = useCallback(() => {
    if (missed !== 'checking') {
      setFailed(true);
    }

    if (onRenderFailure) {
      onRenderFailure();
    }
  }, [missed, onRenderFailure]);

  const FB: FC<IFallbackProps> = () => {
    return Fallback ? (
      <Fallback />
    ) : (
      <UnknownPreview fallbackTitle={rest.fallbackTitle} onRenderFailure={handleRenderFailure} isPackage={isPackage} />
    );
  };

  const check: CheckFileRequest | undefined = useMemo(() => {
    if (!shouldCheck) {
      setMissed(propsMissed ?? false);
      return undefined;
    }
    if (propsMissed !== undefined) {
      setMissed(propsMissed);
      return undefined;
    }
    if (typeof source === 'string') {
      return new PresignedUrl({ url: source });
    }
    if (source instanceof FileRevisionID) {
      return source;
    }
  }, [shouldCheck, propsMissed, source]);

  const { data, isSuccess } = useCheckFile(
    { check },
    {
      enabled: !!check,
      meta: {
        onError: (err: GenericFailure) => {
          if (err instanceof GenericFailure && err.code === GenericFailureCode.EntityNotFound) {
            setMissed(true);
            onCheck?.(FileState.NotFound);
          }
        },
      },
    }
  );

  useEffect(() => {
    if (!isSuccess || !data) return;
    setMissed(data.state === FileState.NotFound);
    onCheck?.(data.state);
  }, [isSuccess, data, onCheck]);

  const handleDialogOpen = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (onClick !== undefined) {
        onClick(event);
      }
      if (!failed && missed === false && allowFullPreview) {
        setFullPreview(true);
      }
    },
    [failed, missed, onClick, allowFullPreview]
  );

  const handleDialogClose = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      if (allowFullPreview) {
        setFullPreview(false);
      }
    },
    [allowFullPreview, setFullPreview]
  );

  const Wrapper =
    failed || missed !== false
      ? Fragment
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ({ children }: ComponentProps<any>) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return showTooltip ? (
            <Tooltip placement={tooltipPlacement} title={tooltip || 'Click to full preview'}>
              {children}
            </Tooltip>
          ) : (
            children
          );
        };

  const handleWrapperClick = !!onClick || allowFullPreview ? handleDialogOpen : undefined;

  return (
    <>
      <Wrapper>
        <TooltipContainerStyled>
          {thumbnails?.length ? (
            <ThumbnailPreview
              {...rest}
              missed={missed}
              thumbnails={thumbnails}
              Fallback={FB}
              onClick={handleWrapperClick}
            />
          ) : (
            <SourcePreview
              {...rest}
              missed={missed}
              source={source}
              Fallback={FB}
              sourceName={sourceName}
              onClick={handleWrapperClick}
            />
          )}
        </TooltipContainerStyled>
      </Wrapper>

      {fullPreview && (
        <FullScreenPreview
          {...rest}
          source={source}
          onRenderFailure={onRenderFailure}
          Fallback={FB}
          onRebuildThumbnail={onRebuildThumbnail}
          onClose={handleDialogClose}
        />
      )}
    </>
  );
});
